import { useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

// What a server render can never produce and no value seeds safely: a third-party widget, a
// measurement that needs a real layout, a WebGL canvas with no painted stand-in. Most
// browser-only state instead seeds one value on both the server and the first hydration pass
// and adjusts it in an effect (`useReducedMotion`, `useShaderSupport`, ADR-027 point 4); reach
// for `ClientOnly` or `Portal` only when there is truly nothing to seed with.

let hydrated = false;

/** Subscribing is itself the signal: the first commit that can subscribe is already past
 * hydration, so it flips the flag and asks for one more render to read it, for every instance
 * mounted in that same commit, never a `setState` racing the render that caused it. */
function subscribeHydrated(onStoreChange: () => void): () => void {
  hydrated = true;
  queueMicrotask(onStoreChange);
  return () => undefined;
}

const getHydrated = () => hydrated;
const getServerHydrated = () => false;

/** True once the client has taken over from the prerendered markup; false on the server and on
 * the hydration pass that must match it, so nothing it gates ever mismatches (React #418). */
export function useHydrated(): boolean {
  return useSyncExternalStore(subscribeHydrated, getHydrated, getServerHydrated);
}

export type ClientOnlyProps = {
  readonly children: ReactNode;
  /** What shows on the server and until hydration finishes; `null` by default. */
  readonly fallback?: ReactNode;
};

/** Renders `children` only once the client has taken over; `fallback` (nothing, by default)
 * renders on the server and for the hydration pass that must match it. */
export function ClientOnly({ children, fallback = null }: ClientOnlyProps) {
  return useHydrated() ? children : fallback;
}

export type PortalProps = {
  readonly children: ReactNode;
  /** Where to mount; the document body by default. */
  readonly container?: Element;
};

/**
 * Renders children into another part of the DOM (the document body, by default): a server has
 * none to portal into, so this renders nothing until the client has taken over, exactly like
 * `ClientOnly`. Radix's own dialogs, drawers and menus carry their portal already; reach for
 * this one for a feature's own overlay.
 */
export function Portal({ children, container }: PortalProps) {
  if (!useHydrated()) return null;
  return createPortal(children, container ?? document.body);
}
